<template>
  <div class="flex items-center w-full h-full">
    <!-- Toggle header button -->
    <button
      v-if="!sessionStore.isClinician"
      @click="toggleHeader"
      class="absolute w-full h-0 flex justify-center top-29 cursor-pointer z-30"
      type="button"
      v-tippy="{
        content: headerActive ? 'Hide header' : 'Show header',
        theme: 'light-border',
        placement: 'bottom',
        interactive: true,
      }"
    >
      <CircleArrowDown class="relative h-6 w-6 bottom-8" transform="0.3s" :isUp="!headerActive" />
    </button>
    <!-- Header -->
    <div class="flex-grow">
      <div class="flex justify-between items-center w-full h-full">
        <!-- Session title -->
        <section>
          <div class="ml-4">
            <h1 class="prose-md 2xl:prose-xl font-bold leading-7">{{ sessionStore.title }}</h1>
            <span class="text-sm leading-5 font-medium">
              {{ yearFormatter(sessionStore.date) }}
            </span>
          </div>
        </section>

        <!-- Phases -->
        <PhasesWizard
          :endPhase="endPhase"
          :backToPreviousPhase="backToPreviousPhase"
          class="step-user-control-panel"
        />

        <!-- Mute All, Support, Chat -->
        <section class="inline-flex justify-end items-center mr-2">
          <!-- Mute all -->
          <div v-if="sessionStore.isClinician && sessionStore.hasLivePhase" class="flex pr-3">
            <span class="text-sm leading-5 font-medium text-medium-grey">Mute all</span>
            <div
              :data-cy="`mute-all-${!isMuted}`"
              @click="toggleMuteAll"
              v-tippy="{
                content:
                  'By toggling this button, you will mute all participants that are currently unmuted. Participants will still be able to unmute after you have muted them!',
                theme: 'light-border',
                interactive: true,
              }"
            >
              <Toggle class="mx-3" :enabled="!isMuted" />
            </div>
          </div>

          <!-- Support -->
          <div class="flex pr-2">
            <div
              class="cursor-pointer"
              @click="openIntercom"
              v-tippy="{
                content: 'Get help',
                theme: 'light-border',
                interactive: true,
              }"
            >
              <QuestionIcon />
            </div>
          </div>

          <!-- Chat -->
          <div
            v-if="!hideChatBtn"
            class="flex pl-3"
            v-tippy="{
              content: sessionStore.isOpenSidebar ? 'Close chat' : 'Open chat',
              theme: 'light-border',
              interactive: true,
            }"
          >
            <button
              :data-cy="`sidebar-${sessionStore.isOpenSidebar}`"
              class="items-center"
              @click="sessionStore.toggleSidebar"
            >
              <ChatMessagesIcon
                class="h-9 w-9"
                :class="{
                  'text-vice-teal-500': sessionStore.isOpenSidebar,
                  'hover:text-vice-light-grey': !sessionStore.isOpenSidebar,
                }"
              />
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { show } from '@intercom/messenger-js-sdk';
import { directive } from 'vue-tippy';
import Toggle from '@/Components/Inputs/Toggle.vue';
import PhasesWizard from '@/Components/Session/Header/PhasesWizard.vue';
import DateFormatMixinVue from '@/Mixins/DateFormatMixin.vue';
import { PHASE_STATUS, VICE_SESSION_PHASES } from '@/Utils/vice-constants';
import useSessionStore from '@/Stores/useSessionStore';
import QuestionIcon from '@/Components/Icons/QuestionIcon.vue';
import ChatMessagesIcon from '@/Components/Icons/ChatMessagesIcon.vue';
import CircleArrowDown from '@/Components/Icons/CircleArrowDown.vue';

export default {
  props: {
    hideChatBtn: {
      type: Boolean,
      default: false,
    },
    endPhase: {
      type: Function,
    },
    headerActive: {
      type: Boolean,
      default: true,
    },
    toggleHeader: {
      type: Function,
      required: false,
    },
    backToPreviousPhase: {
      type: Function,
    },
    muteParticipants: {
      type: Function,
    },
    pauseCurrentPhase: {
      type: Function,
    },
    turnOffSmartGlass: {
      type: Function,
    },
  },
  directives: {
    tippy: directive,
  },
  mixins: [DateFormatMixinVue],
  components: {
    CircleArrowDown,
    ChatMessagesIcon,
    Toggle,
    PhasesWizard,
    QuestionIcon,
  },

  setup(props) {
    const isMuted = ref(true);
    const isLeaving = ref(false);

    const sessionStore = useSessionStore();
    const phases = sessionStore.phases;

    const toggleMuteAll = () => {
      isMuted.value = !isMuted.value;
      props.muteParticipants(isMuted.value);
    };

    const togglePutOnHold = (isOnHold) => {
      sessionStore.isPutOnHold = isOnHold;
    };

    const openIntercom = () => {
      show();
    };

    return {
      PHASE_STATUS,
      VICE_SESSION_PHASES,
      phases,
      isMuted,
      isLeaving,
      sessionStore,
      openIntercom,
      toggleMuteAll,
      togglePutOnHold,
    };
  },
};
</script>

<style lang="scss" scoped>
.middle {
  max-width: 100%;
}
.negative-mt-5px {
  margin-top: -5px;
}

.top-29 {
  top: 7.25rem;
}
</style>
